import { CONFIG } from "./config";

interface AuthResult {
  AuthenticationResult?: {
    IdToken?: string;
    RefreshToken?: string;
    ExpiresIn?: number;
  };
}

const STORAGE_KEYS = {
  id: "h26.idToken",
  rt: "h26.refreshToken",
  exp: "h26.idTokenExp",
};

export class Auth {
  static loadIdToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.id);
  }
  static loadExp(): number | null {
    const v = localStorage.getItem(STORAGE_KEYS.exp);
    return v ? Number(v) : null;
  }
  static loadRefresh(): string | null {
    return localStorage.getItem(STORAGE_KEYS.rt);
  }
  static signOut() {
    Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
  }

  static async signIn(username: string, password: string) {
    const json = await Auth.cognito({
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: CONFIG.cognitoClientId,
      AuthParameters: { USERNAME: username, PASSWORD: password },
    });
    Auth.persist(json);
  }

  static async refresh() {
    const rt = Auth.loadRefresh();
    if (!rt) throw new Error("No refresh token");
    const json = await Auth.cognito({
      AuthFlow: "REFRESH_TOKEN_AUTH",
      ClientId: CONFIG.cognitoClientId,
      AuthParameters: { REFRESH_TOKEN: rt },
    });
    Auth.persist(json, rt);
  }

  static async currentIdToken(): Promise<string> {
    const id = Auth.loadIdToken();
    const exp = Auth.loadExp();
    if (id && exp && exp > Date.now() + 60_000) return id;
    if (Auth.loadRefresh()) {
      await Auth.refresh();
      const fresh = Auth.loadIdToken();
      if (fresh) return fresh;
    }
    throw new Error("Not signed in");
  }

  // ── private ────────────────────────────────────────────────────────────

  private static async cognito(body: object): Promise<AuthResult> {
    const res = await fetch(`https://cognito-idp.${CONFIG.cognitoRegion}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`Cognito ${res.status}: ${t}`);
    }
    return res.json() as Promise<AuthResult>;
  }

  private static persist(json: AuthResult, keepRefreshToken?: string) {
    const a = json.AuthenticationResult;
    if (!a?.IdToken) throw new Error("No IdToken returned");
    localStorage.setItem(STORAGE_KEYS.id, a.IdToken);
    const rt = a.RefreshToken ?? keepRefreshToken;
    if (rt) localStorage.setItem(STORAGE_KEYS.rt, rt);
    const exp = Date.now() + (a.ExpiresIn ?? 3600) * 1000;
    localStorage.setItem(STORAGE_KEYS.exp, String(exp));
  }
}
